// src/services/groupQuality.js — how much of your uplink each person in the call gets.
//
// 🟥 THE BROWSER'S GROUP CALL HAD NO QUALITY MANAGEMENT AT ALL. Not a weak one —
//    none. In a mesh every participant sends a SEPARATE copy of your camera to
//    every other participant, so a four-person call is three full-rate uploads
//    from one laptop. On anything but excellent wifi that saturates the uplink,
//    and the first thing to die is the audio everybody actually needs.
//
// 🟢 SO THE BUDGET IS TOTAL, NOT PER-STREAM. The phone has worked this way for a
//    while — ~2 Mbps of upload divided by however many people are in the room,
//    capped per stream — and the numbers are mirrored rather than re-guessed so
//    a phone and a browser in the same call behave the same way.
//
// 🟢 AND A MESH CAN DO SOMETHING AN SFU CANNOT WITHOUT SIMULCAST: send a
//    DIFFERENT encoding to each person. The one whose tile you have pinned is
//    worth more of the budget than the six thumbnails nobody is looking at, and
//    in a mesh that is simply a different maxBitrate on that leg. Zoom and Meet
//    need simulcast layers and an SFU to approximate this.

/* ── the phone's numbers (server/GroupWebRTCService.ts) ────────────────────── */
export const TOTAL_BUDGET_BPS = 2_000_000;   // ~2 Mbps of upload, all peers together
export const MAX_PER_STREAM_BPS = 600_000;   // no single leg gets more than this
export const START_BPS = 150_000;            // gentle start; the ramp earns the rest
/* the phone's thresholds, verbatim — a browser and a phone in the same call
   should step resolution at the same points */
export const T_360 = 600_000;                // below this, quarter resolution
export const T_720 = 1_200_000;              // below this, half

/**
 * The ceiling for one leg, given how many people are in the room.
 *
 * 🟥 DIVIDED, NOT FIXED. A per-stream cap alone is not a budget: five peers at
 *    600 kbps each is 3 Mbps of upload from a laptop that may have 1.
 */
export function perStreamMax(peerCount, opts = {}) {
  const n = Math.max(1, Number(peerCount) || 1);
  const total = Number(opts.total) || TOTAL_BUDGET_BPS;
  const cap = Number(opts.cap) || MAX_PER_STREAM_BPS;
  return Math.min(cap, Math.round(total / n));
}

/**
 * 🟢 THE PINNED PERSON IS WORTH MORE THAN A THUMBNAIL. `share` is what fraction
 *    of the budget this leg deserves — the mesh's advantage, spent deliberately.
 */
export const TIER = { pinned: 1.0, normal: 0.6, thumb: 0.3, hidden: 0.12 };

export function legBudget(peerCount, tier = 'normal', opts = {}) {
  const base = perStreamMax(peerCount, opts);
  const share = TIER[tier] ?? TIER.normal;
  /* 🟥 A FLOOR, BECAUSE A TILE NOBODY IS LOOKING AT IS STILL A FACE. Below about
     60 kbps video stops being recognisable and the bandwidth is wasted either
     way — better to send a small clean picture than a large broken one. */
  return Math.max(60_000, Math.round(base * share));
}

/**
 * How far to scale the capture down for a given bitrate.
 *
 * 🟥 DOWN IMMEDIATELY, UP ONLY WITH HEADROOM. A resolution change costs a
 *    keyframe, so a rule that steps up the instant it is allowed will oscillate
 *    across the boundary and spend more on keyframes than it gains in pixels.
 *    Dropping is urgent; climbing can wait for 15% of margin.
 */
export function nextScale(bps, current = 4) {
  const b = Number(bps) || 0;
  const cur = Number(current) || 4;
  const target = b < T_360 ? 4 : b < T_720 ? 2 : 1;
  if (target >= cur) return target;                  // smaller picture: at once
  const boundary = target === 1 ? T_720 : T_360;     // bigger: only with headroom
  return b >= boundary * 1.15 ? target : cur;
}

/** Frame rate follows resolution — a small picture does not need 30fps. */
export function fpsFor(scale) {
  const s = Number(scale) || 1;
  return s >= 4 ? 20 : s >= 2 ? 24 : 30;
}
